import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const LeadForm = ({ onSuccess, isLightMode, draftProducts }) => {
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);

  // Helper for multi-select checkboxes
  const [buyerTypes, setBuyerTypes] = useState([]);
  const [salesChannels, setSalesChannels] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [brandsInterest, setBrandsInterest] = useState([]);
  const [nextSteps, setNextSteps] = useState([]);

  const toggleSelection = (state, setState, value) => {
    if (state.includes(value)) {
      setState(state.filter(item => item !== value));
    } else {
      setState([...state, value]);
    }
  };
  // Draft Products Handling
  React.useEffect(() => {
      if (draftProducts?.length > 0) {
          const skuList = draftProducts.map(p => 
              `${p.sku} - ${p.title} (x${p.quantity}) - £${p.special_price || p.price_point}`
          ).join("\n");
          
          // Pre-fill SKUs text area
          reset({
              skus_interest: skuList
          });
      }
  }, [draftProducts, reset]);

  const onSubmit = async (data) => {
    setLoading(true);
    
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("trade_token");

    const payload = {
        ...data,
        buyer_type: buyerTypes,
        sales_channels: salesChannels,
        territories: territories,
        brands_interest: brandsInterest,
        next_steps: nextSteps,
        has_physical_stores: data.has_physical_stores === "yes"
    };

    // If we have draft products, we need to structure them properly
    if (draftProducts?.length > 0) {
        payload.interested_products = draftProducts;
        // Ensure SKUs interest text doesn't override structural data if user didn't edit it
        if (!payload.skus_interest) payload.skus_interest = "See attached items";
    }

    try {
      await axios.post(`${BACKEND_URL}/api/leads`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Lead Captured", { description: "Form submitted successfully." });
      // Reset logic
      reset();
      setBuyerTypes([]);
      setSalesChannels([]);
      setTerritories([]);
      setBrandsInterest([]);
      setNextSteps([]);
      
      // Trigger refresh in parent
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error(error);
      toast.error("Submission Failed", { description: "Could not save lead." });
    } finally {
      setLoading(false);
    }
  };

  const cardClass = `space-y-6 border p-8 rounded-sm ${isLightMode ? 'bg-white border-gray-200' : 'bg-dreibach-card border-white/10'}`;
  const inputClass = `rounded-none ${isLightMode ? 'bg-gray-50 border-gray-300 text-gray-900 focus:border-dreibach-teal' : 'bg-black/50 border-white/10 text-white focus:border-dreibach-teal'}`;
  const labelClass = `${isLightMode ? 'text-gray-500' : 'text-gray-400'} uppercase text-xs font-bold`;
  const headingClass = `text-xl font-black uppercase tracking-widest border-b pb-4 mb-6 ${isLightMode ? 'text-gray-900 border-gray-200' : 'text-white border-white/10'}`;
  const textClass = isLightMode ? 'text-gray-700' : 'text-white';

  return (
    <div className="max-w-4xl mx-auto py-8">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-12">
        
        {/* LEAD DETAILS */}
        <section className={cardClass}>
           <h3 className={headingClass}>Lead Details</h3>
           <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                 <Label className={labelClass}>Name</Label>
                 <Input {...register("name", { required: true })} className={inputClass} />
              </div>
              <div className="space-y-2">
                 <Label className={labelClass}>Job Title</Label>
                 <Input {...register("job_title")} className={inputClass} />
              </div>
              <div className="space-y-2">
                 <Label className={labelClass}>Company</Label>
                 <Input {...register("company", { required: true })} className={inputClass} />
              </div>
              <div className="space-y-2">
                 <Label className={labelClass}>Website / LinkedIn</Label>
                 <Input {...register("website")} className={inputClass} />
              </div>
              <div className="space-y-2">
                 <Label className={labelClass}>Email</Label>
                 <Input {...register("email", { required: true })} type="email" className={inputClass} />
              </div>
              <div className="space-y-2">
                 <Label className={labelClass}>Phone / WhatsApp</Label>
                 <Input {...register("phone")} className={inputClass} />
              </div>
              <div className="space-y-2">
                 <Label className={labelClass}>Country</Label>
                 <Input {...register("country")} className={inputClass} />
              </div>
           </div>
        </section>

        {/* BUYER PROFILE */}
        <section className={cardClass}>
           <h3 className={headingClass}>Buyer Profile</h3>
           
           <div className="space-y-4 mb-6">
              <Label className="text-dreibach-teal uppercase text-xs font-bold">Buyer Type</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                 {["Retailer", "Distributor", "Wholesaler", "Online Seller"].map(type => (
                    <div key={type} className="flex items-center space-x-2">
                       <Checkbox id={type} checked={buyerTypes.includes(type)} onCheckedChange={() => toggleSelection(buyerTypes, setBuyerTypes, type)} className={`data-[state=checked]:bg-dreibach-teal ${isLightMode ? 'border-gray-300' : 'border-white/20'}`} />
                       <Label htmlFor={type} className={`${textClass} text-sm`}>{type}</Label>
                    </div>
                 ))}
              </div>
           </div>

           <div className="space-y-4 mb-6">
              <Label className="text-dreibach-teal uppercase text-xs font-bold">Sales Channels</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                 {["Amazon", "Shopify", "eBay", "TikTok Shop", "Own Site", "Wholesale"].map(channel => (
                    <div key={channel} className="flex items-center space-x-2">
                       <Checkbox id={channel} checked={salesChannels.includes(channel)} onCheckedChange={() => toggleSelection(salesChannels, setSalesChannels, channel)} className={`data-[state=checked]:bg-dreibach-teal ${isLightMode ? 'border-gray-300' : 'border-white/20'}`} />
                       <Label htmlFor={channel} className={`${textClass} text-sm`}>{channel}</Label>
                    </div>
                 ))}
              </div>
           </div>

           <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                 <Label className="text-dreibach-teal uppercase text-xs font-bold">Physical Stores?</Label>
                 <RadioGroup defaultValue="no" className="flex gap-4" onValueChange={(val) => register("has_physical_stores").onChange({target: {value: val}})}>
                    <div className="flex items-center space-x-2">
                       <RadioGroupItem value="yes" id="store-yes" className={`text-dreibach-teal ${isLightMode ? 'border-gray-300' : 'border-white/20'}`} />
                       <Label htmlFor="store-yes" className={textClass}>Yes</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                       <RadioGroupItem value="no" id="store-no" className={`text-dreibach-teal ${isLightMode ? 'border-gray-300' : 'border-white/20'}`} />
                       <Label htmlFor="store-no" className={textClass}>No</Label>
                    </div>
                 </RadioGroup>
                 <Input {...register("store_count")} placeholder="Number of stores" className={`${inputClass} mt-2`} />
              </div>

              <div className="space-y-4">
                 <Label className="text-dreibach-teal uppercase text-xs font-bold">Territories</Label>
                 <div className="flex gap-4">
                    {["UK", "EU", "Other"].map(terr => (
                        <div key={terr} className="flex items-center space-x-2">
                           <Checkbox id={terr} checked={territories.includes(terr)} onCheckedChange={() => toggleSelection(territories, setTerritories, terr)} className={`data-[state=checked]:bg-dreibach-teal ${isLightMode ? 'border-gray-300' : 'border-white/20'}`} />
                           <Label htmlFor={terr} className={`${textClass} text-sm`}>{terr}</Label>
                        </div>
                    ))}
                 </div>
              </div>
           </div>
        </section>

                 {draftProducts?.length > 0 && (
                     <div className="mb-2 p-3 bg-dreibach-teal/10 border border-dreibach-teal/20 rounded-sm">
                         <p className="text-dreibach-teal text-xs font-bold uppercase mb-1">
                             {draftProducts.length} Items Attached from Quote
                         </p>
                         <p className="text-[10px] opacity-70">These will be automatically added to the lead.</p>
                     </div>
                 )}
        {/* INTEREST & POTENTIAL */}
        <section className={cardClass}>
           <h3 className={headingClass}>Interest & Order Potential</h3>
           
           <div className="space-y-4 mb-8">
              <Label className="text-dreibach-teal uppercase text-xs font-bold">Brands of Interest</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                 {["GloBrite", "Tanness", "Pelle & Sol", "Yum Yum", "Fur & Bone", "La Donna"].map(brand => (
                    <div key={brand} className="flex items-center space-x-2">
                       <Checkbox id={brand} checked={brandsInterest.includes(brand)} onCheckedChange={() => toggleSelection(brandsInterest, setBrandsInterest, brand)} className={`data-[state=checked]:bg-dreibach-teal ${isLightMode ? 'border-gray-300' : 'border-white/20'}`} />
                       <Label htmlFor={brand} className={`${textClass} text-sm`}>{brand}</Label>
                    </div>
                 ))}
              </div>
           </div>

           <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-2">
                 <Label className={labelClass}>SKUs / Products</Label>
                 <Textarea {...register("skus_interest")} placeholder="List SKUs or circle catalogue items..." className={`${inputClass} h-24`} />
              </div>
              <div className="space-y-4">
                 <div className="space-y-2">
                    <Label className={labelClass}>Est. First Order Volume</Label>
                    <Input {...register("estimated_volume")} placeholder="Units / Cartons" className={inputClass} />
                 </div>
                 <div className="space-y-2">
                    <Label className={labelClass}>Target Retail Price</Label>
                    <Input {...register("target_price_range")} placeholder="£ - £" className={inputClass} />
                 </div>
              </div>
           </div>

           <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                 <Label className="text-dreibach-teal uppercase text-xs font-bold">Timing</Label>
                 <RadioGroup onValueChange={(val) => register("order_timing").onChange({target: {value: val}})} className="grid grid-cols-2 gap-2">
                    {["ASAP", "30 Days", "60-90 Days", "Later"].map(time => (
                       <div key={time} className="flex items-center space-x-2">
                          <RadioGroupItem value={time} id={time} className={`text-dreibach-teal ${isLightMode ? 'border-gray-300' : 'border-white/20'}`} />
                          <Label htmlFor={time} className={`${textClass} text-sm`}>{time}</Label>
                       </div>
                    ))}
                 </RadioGroup>
              </div>
              <div className="space-y-4">
                 <Label className="text-dreibach-teal uppercase text-xs font-bold">Decision Maker?</Label>
                 <RadioGroup onValueChange={(val) => register("decision_maker").onChange({target: {value: val}})} className="flex gap-4">
                    <div className="flex items-center space-x-2">
                       <RadioGroupItem value="Yes" id="dm-yes" className={`text-dreibach-teal ${isLightMode ? 'border-gray-300' : 'border-white/20'}`} />
                       <Label htmlFor="dm-yes" className={textClass}>Yes</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                       <RadioGroupItem value="Influencer" id="dm-inf" className={`text-dreibach-teal ${isLightMode ? 'border-gray-300' : 'border-white/20'}`} />
                       <Label htmlFor="dm-inf" className={textClass}>Influencer</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                       <RadioGroupItem value="No" id="dm-no" className={`text-dreibach-teal ${isLightMode ? 'border-gray-300' : 'border-white/20'}`} />
                       <Label htmlFor="dm-no" className={textClass}>No</Label>
                    </div>
                 </RadioGroup>
              </div>
           </div>
        </section>

        {/* NEXT STEPS */}
        <section className={cardClass}>
           <h3 className={headingClass}>Next Steps</h3>
           
           <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                 {["Send price list", "Send full catalogue", "Send samples", "Book follow-up call"].map(step => (
                    <div key={step} className="flex items-center space-x-2">
                       <Checkbox id={step} checked={nextSteps.includes(step)} onCheckedChange={() => toggleSelection(nextSteps, setNextSteps, step)} className={`data-[state=checked]:bg-dreibach-teal ${isLightMode ? 'border-gray-300' : 'border-white/20'}`} />
                       <Label htmlFor={step} className={`${textClass} text-sm`}>{step}</Label>
                    </div>
                 ))}
              </div>
              <div className="space-y-2">
                 <Label className={labelClass}>Follow-up / Notes</Label>
                 <Textarea {...register("notes")} placeholder="Key requirements (MOQ, margin, packaging)..." className={`${inputClass} h-32`} />
              </div>
           </div>
        </section>

        <div className="flex justify-end">
           <Button type="submit" disabled={loading} className="bg-dreibach-teal text-white hover:bg-white hover:text-black uppercase font-bold tracking-widest px-12 py-6 rounded-none text-lg">
              {loading ? "Saving..." : "Save Lead"}
           </Button>
        </div>

      </form>
    </div>
  );
};

export default LeadForm;
